<template>
  <div>
    <v-card>
      <v-card-title>
        Fitness Assessments Library
        <v-spacer />

        <v-btn v-if="selected.length > 0" class="mr-2 mb-2" color="secondary" @click="showBulkStatusDialog">
          MANAGE SELECTED ({{ selected.length }})
        </v-btn>

        <v-btn class="mb-2" color="primary" dark @click="showEditDialog(blankSessionAssessment)">
          CREATE
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4" offset-md="8">
            <v-text-field
              v-model="dtSearchQuery"
              :append-icon="icons.mdiMagnify"
              label="Search"
              single-line
              hide-details
              dense
              outlined
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        v-model="selected"
        :headers="tableHeaders"
        :items="fitnessSessionAssessments"
        :search="dtSearchQuery"
        :expanded.sync="dtExpanded"
        :loading="isLoading"
        :items-per-page="30"
        :footer-props="{ 'items-per-page-options': [15, 30, 45] }"
        show-select
        single-expand
        show-expand
        item-key="id"
      >
        <template v-slot:item.category="{ item }">
          <v-chip small color="primary" outlined>
            {{ item.category }}
          </v-chip>
        </template>

        <template v-slot:item.groupType="{ item }">
          <v-chip v-if="item.groupType" small color="secondary" outlined>
            {{ getGroupTypeLabel(item.groupType) }}
          </v-chip>
          <span v-else class="grey--text">---</span>
        </template>

        <template v-slot:item.creator="{ item }">
          <div v-if="item.creator" class="text-body-2 py-2" style="line-height: 1.4;" v-html="item.creator"></div>
          <div v-else class="text-caption grey--text font-italic">
            System
          </div>
        </template>

        <template v-slot:item.academy="{ item }">
          <div v-if="item.academy" class="text-body-2 py-2" style="line-height: 1.4;" v-html="item.academy"></div>
          <div v-else class="text-caption grey--text font-italic">
            Global
          </div>
        </template>

        <template v-slot:item.active="{ item }">
          <v-chip class="table-status-chip" :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? 'Yes' : 'No' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon class="mr-2" @click="showEditDialog(item)">
            {{ icons.mdiPencilOutline }}
          </v-icon>
          <v-icon @click="showDeleteConfirmationDialog(item)">
            {{ icons.mdiDeleteOutline }}
          </v-icon>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <fitness-assessments-details :item="item" />
          </td>
        </template>
      </v-data-table>

      <delete-dialog
        v-model="isDeleteDialogVisible"
        :item="sessionAssessment"
        :is-loading="isLoading"
        @cancel="onDeleteCanceled"
        @delete="onDeleteConfirmed"
      />

      <fitness-assessments-edit-dialog
        v-if="isEditDialogVisible"
        v-model="isEditDialogVisible"
        :item="sessionAssessment"
        :is-loading="isLoading"
        @cancel="onEditCanceled"
        @save="onEditSave"
      />

      <v-dialog v-model="isBulkStatusDialogVisible" max-width="720">
        <v-card>
          <v-card-title>Apply to Selected</v-card-title>
          <v-card-text class="pt-4 pb-2">
            <p>Choose an action for {{ selected.length }} selected item(s).</p>
            <v-radio-group v-model="bulkStatusValue" class="mt-4" hide-details>
              <v-radio
                v-for="option in bulkStatusOptions"
                :key="option.value"
                :value="option.value"
                class="mb-2"
              >
                <template v-slot:label>
                  <span :class="option.value === 'delete-selected' ? 'error--text font-weight-medium' : ''">
                    {{ option.text }}
                  </span>
                </template>
              </v-radio>
            </v-radio-group>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="isBulkStatusDialogVisible = false">
              Cancel
            </v-btn>
            <v-btn color="primary" :loading="isBulkUpdating" @click="updateBulkStatus">
              Continue
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <content-pack-bulk-dialogs
        :selected-items="selected"
        :content-pack-options="contentPackOptions"
        :academy-options="academyOptions"
        :selected-content-pack-id.sync="selectedContentPackId"
        :selected-academy-id.sync="selectedAcademyId"
        :is-content-pack-dialog-visible.sync="isContentPackDialogVisible"
        :is-academy-dialog-visible.sync="isAcademyDialogVisible"
        :is-content-pack-confirmation-dialog-visible.sync="isContentPackConfirmationDialogVisible"
        :is-academy-confirmation-dialog-visible.sync="isAcademyConfirmationDialogVisible"
        :is-bulk-updating="isBulkUpdating"
        :content-pack-confirmation-title="contentPackConfirmationTitle"
        :content-pack-confirmation-message="contentPackConfirmationMessage"
        :academy-dialog-title="academyDialogTitle"
        :academy-confirmation-title="academyConfirmationTitle"
        :academy-confirmation-message="academyConfirmationMessage"
        @cancel-selection="hideContentPackDialog"
        @continue-selection="showContentPackConfirmationDialog"
        @close-confirmation="resetContentPackDialogs"
        @confirm-selection="applyContentPackAction"
        @cancel-academy-selection="hideAcademyDialog"
        @continue-academy-selection="showAcademyConfirmationDialog"
        @close-academy-confirmation="resetContentPackDialogs"
        @confirm-academy-selection="applyAcademyAction"
      />

      <v-dialog v-model="isBulkDeleteDialogVisible" max-width="500">
        <v-card>
          <v-card-title>Delete Selected Items</v-card-title>
          <v-card-text>
            <p>Are you sure you want to delete <strong>{{ selected.length }}</strong> items?</p>
            <p class="error--text mb-0">
              This action cannot be undone.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="isBulkDeleteDialogVisible = false">
              Cancel
            </v-btn>
            <v-btn color="error" :loading="isBulkDeleting" @click="deleteBulkItems">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import {
  mdiCheck, mdiClose, mdiDeleteOutline, mdiPencilOutline, mdiMagnify,
} from '@mdi/js'
import { onMounted, ref, computed } from 'vue'
import ContentPackBulkDialogs from '@/components/ContentPackBulkDialogs.vue'
import DeleteDialog from '@/components/DeleteDialog.vue'
import FitnessAssessmentsDetails from '@/views/fitnessAssessments/partials/FitnessAssessmentsDetails.vue'
import useContentPackBulkActions from '@/composables/contentPackBulkActions'
import useSessionAssessments from '@/composables/api/assessments/session'
import useUniqueKey from '@/composables/uniqueKey'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAuth from '@/composables/useAuth'
import { FitnessExercisesSubtype } from '@/enums/enums'

export default {
  components: {
    ContentPackBulkDialogs,
    DeleteDialog,
    FitnessAssessmentsDetails,

    // Dynamic import to avoid circular dependencies if any
    FitnessAssessmentsEditDialog: () => import('@/views/fitnessAssessments/partials/FitnessAssessmentsEditDialog.vue'),
  },
  setup() {
    // API and Composables
    const {
      dtHeaders,
      dtExpanded,
      dtSearchQuery,
      blankSessionAssessment,
      sessionAssessment,
      sessionAssessments,
      isLoading,
      createSessionAssessment,
      deleteSessionAssessment,
      fetchSessionAssessments,
      updateSessionAssessment,
      updateManySessionAssessments,
    } = useSessionAssessments()

    const { uniqueKey } = useUniqueKey()
    const { showSnackbar } = useSnackbar()
    const { hasRole } = useAuth()

    // Local State
    const selected = ref([])
    const isDeleteDialogVisible = ref(false)
    const isEditDialogVisible = ref(false)

    const isBulkStatusDialogVisible = ref(false)
    const bulkStatusValue = ref('active-true')
    const isBulkUpdating = ref(false)
    const {
      isContentPackDialogVisible,
      isContentPackConfirmationDialogVisible,
      isAcademyDialogVisible,
      isAcademyConfirmationDialogVisible,
      selectedContentPackId,
      selectedAcademyId,
      contentPackOptions,
      academyOptions,
      contentPackConfirmationTitle,
      contentPackConfirmationMessage,
      academyDialogTitle,
      academyConfirmationTitle,
      academyConfirmationMessage,
      hideContentPackDialog,
      hideAcademyDialog,
      showContentPackConfirmationDialog,
      showAcademyConfirmationDialog,
      beginBulkAction,
      applyContentPackAction: saveContentPackAction,
      applyAcademyAction: saveAcademyAction,
      resetContentPackDialogs,
    } = useContentPackBulkActions({
      selectedItems: selected,
      sectionKey: 'fitness',
      subsectionKey: 'assessments',
      libraryType: 'FITNESS_ASSESSMENT',
      updateManyItems: updateManySessionAssessments,
    })
    const bulkStatusOptions = [
      { text: 'Active', value: 'active-true' },
      { text: 'Inactive', value: 'active-false' },
      { text: 'Delete Selected', value: 'delete-selected' },
      { text: 'Assign to Content Pack', value: 'content-pack-add' },
      { text: 'Remove from Content Pack', value: 'content-pack-delete' },
      { text: 'Include in Academy', value: 'academy-include' },
      { text: 'Exclude from Academy', value: 'academy-exclude' },
    ]
    const bulkStatusConfig = {
      'active-true': { paramName: 'active', paramValue: true },
      'active-false': { paramName: 'active', paramValue: false },
    }

    const isBulkDeleteDialogVisible = ref(false)
    const isBulkDeleting = ref(false)

    // Computed Logic
    const isAdmin = computed(() => hasRole(['ADMIN']))

    const tableHeaders = computed(() => {
      // Safety check: if dtHeaders or dtHeaders.value doesn't exist
      if (!dtHeaders || !dtHeaders.value) return []

      // Filter out creator column for non-admins
      const headers = isAdmin.value
        ? dtHeaders.value
        : dtHeaders.value.filter(h => h.value !== 'creator' && h.value !== 'academy')

      const creatorIndex = headers.findIndex(h => h.value === 'creator')
      const insertIndex = creatorIndex !== -1 ? creatorIndex : headers.findIndex(h => h.value === 'active')
      const groupTypeHeader = {
        text: 'TYPE',
        value: 'groupType',
        sortable: true,
        width: 150,
      }

      if (insertIndex === -1) return [...headers, groupTypeHeader]

      const nextHeaders = [...headers]
      nextHeaders.splice(insertIndex, 0, groupTypeHeader)

      return nextHeaders
    })

    const fitnessSessionAssessments = computed(() => sessionAssessments.value.filter(a => a.category?.toLowerCase() === 'fitness'))

    const groupTypeLabels = {
      [FitnessExercisesSubtype.STABILITY]: 'Stability/Mobility',
      [FitnessExercisesSubtype.STRENGTH]: 'Strength',
      [FitnessExercisesSubtype.SPEED]: 'Speed',
      [FitnessExercisesSubtype.POWER]: 'Power',
      [FitnessExercisesSubtype.CARDIO]: 'Cardio',
      [FitnessExercisesSubtype.MENTAL]: 'Mental',
    }

    const getGroupTypeLabel = groupType => groupTypeLabels[groupType] || groupType

    // Actions
    onMounted(fetchSessionAssessments)

    const showDeleteConfirmationDialog = a => {
      sessionAssessment.value = a
      isDeleteDialogVisible.value = true
    }

    const onDeleteCanceled = () => {
      sessionAssessment.value = structuredClone(blankSessionAssessment)
      isDeleteDialogVisible.value = false
    }

    const onDeleteConfirmed = async a => {
      await deleteSessionAssessment(a)
      await fetchSessionAssessments()
      isDeleteDialogVisible.value = false
    }

    const showEditDialog = itemData => {
      const item = structuredClone(itemData)
      if (!item.uniqueKey) item.uniqueKey = uniqueKey()

      // Map sessions structure to a flat list for the editor if needed
      if ((!item.assessmentsList || item.assessmentsList.length === 0) && item.sessions?.length > 0) {
        item.assessmentsList = []
        item.sessions.forEach(s => {
          (s.assessments || []).forEach(a => {
            item.assessmentsList.push({
              ...a,
              sessionType: s.type,
              sessionCategory: s.category,
              exercise: a.exercise || { uniqueKey: '', name: '' },
            })
          })
        })
      }

      sessionAssessment.value = item
      isEditDialogVisible.value = true
    }

    const onEditCanceled = () => {
      sessionAssessment.value = structuredClone(blankSessionAssessment)
      isEditDialogVisible.value = false
    }

    const onEditSave = async s => {
      try {
        if (!s.id) {
          await createSessionAssessment(s)
        } else {
          await updateSessionAssessment(s)
        }
        await fetchSessionAssessments()
        isEditDialogVisible.value = false
      } catch (error) {
        console.error(error)
      }
    }

    // Bulk Logic
    const showBulkStatusDialog = () => { isBulkStatusDialogVisible.value = true }
    const applyContentPackAction = async () => {
      isBulkUpdating.value = true
      try {
        const saved = await saveContentPackAction()
        if (!saved) return
        await fetchSessionAssessments()
        selected.value = []
        isBulkStatusDialogVisible.value = false
        showSnackbar('Content pack updated successfully', SnackbarType.success)
      } catch (error) {
        showSnackbar('Failed to update content pack', SnackbarType.error)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const applyAcademyAction = async () => {
      isBulkUpdating.value = true
      try {
        const saved = await saveAcademyAction()
        if (!saved) return
        await fetchSessionAssessments()
        selected.value = []
        isBulkStatusDialogVisible.value = false
        showSnackbar('Academy updated successfully', SnackbarType.success)
      } catch (error) {
        showSnackbar('Failed to update academy', SnackbarType.error)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const updateBulkStatus = async () => {
      if (selected.value.length === 0) return
      if (bulkStatusValue.value === 'delete-selected') {
        isBulkStatusDialogVisible.value = false
        isBulkDeleteDialogVisible.value = true

        return
      }
      isBulkUpdating.value = true
      try {
        if (await beginBulkAction(bulkStatusValue.value)) {
          isBulkUpdating.value = false

          return
        }

        const bulkUpdate = bulkStatusConfig[bulkStatusValue.value]
        if (!bulkUpdate) return

        await updateManySessionAssessments('FITNESS_ASSESSMENT', bulkUpdate.paramName, bulkUpdate.paramValue, selected.value.map(i => i.id))
        await fetchSessionAssessments()
        selected.value = []
        isBulkStatusDialogVisible.value = false
        showSnackbar('Items updated successfully', SnackbarType.success)
      } catch (error) {
        showSnackbar('Error updating items', SnackbarType.error)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const showBulkDeleteDialog = () => { isBulkDeleteDialogVisible.value = true }

    const deleteBulkItems = async () => {
      if (selected.value.length === 0) return
      isBulkDeleting.value = true
      try {
        await Promise.all(selected.value.map(item => deleteSessionAssessment(item)))
        await fetchSessionAssessments()
        selected.value = []
        isBulkDeleteDialogVisible.value = false
        showSnackbar('Items deleted successfully', SnackbarType.success)
      } catch (error) {
        showSnackbar('Error deleting items', SnackbarType.error)
      } finally {
        isBulkDeleting.value = false
      }
    }

    return {
      tableHeaders,
      dtExpanded,
      dtSearchQuery,
      blankSessionAssessment,
      sessionAssessments,
      fitnessSessionAssessments,
      getGroupTypeLabel,
      sessionAssessment,
      selected,
      isBulkStatusDialogVisible,
      bulkStatusValue,
      bulkStatusOptions,
      isBulkUpdating,
      showBulkStatusDialog,
      isContentPackDialogVisible,
      isContentPackConfirmationDialogVisible,
      isAcademyDialogVisible,
      isAcademyConfirmationDialogVisible,
      selectedContentPackId,
      selectedAcademyId,
      contentPackOptions,
      academyOptions,
      contentPackConfirmationTitle,
      contentPackConfirmationMessage,
      academyDialogTitle,
      academyConfirmationTitle,
      academyConfirmationMessage,
      hideContentPackDialog,
      hideAcademyDialog,
      showContentPackConfirmationDialog,
      showAcademyConfirmationDialog,
      applyContentPackAction,
      applyAcademyAction,
      resetContentPackDialogs,
      updateBulkStatus,
      isBulkDeleteDialogVisible,
      isBulkDeleting,
      showBulkDeleteDialog,
      deleteBulkItems,
      isDeleteDialogVisible,
      showDeleteConfirmationDialog,
      onDeleteCanceled,
      onDeleteConfirmed,
      isEditDialogVisible,
      showEditDialog,
      onEditCanceled,
      onEditSave,
      isLoading,
      icons: {
        mdiCheck, mdiClose, mdiDeleteOutline, mdiPencilOutline, mdiMagnify,
      },
    }
  },
}
</script>
